import { InjectionToken } from '@angular/core';

// Canonical client id (x-ub-client) for this build. Defaults to 'booking' (Urban Air); each brand
// app overrides it — e.g. { provide: UB_CLIENT, useValue: 'pma-booking' } from its environment.
export const UB_CLIENT = new InjectionToken<string>('UB_CLIENT', {
  providedIn: 'root',
  factory: () => 'booking',
});

const SESSION_KEY = 'ub_session';
const REF_KEY = 'ub_ref';
const CAMPAIGN_KEY = 'ub_campaign';
const LANDING_KEY = 'ub_landing';
const SENT_KEY = 'ub_session_sent';

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null; // private mode / disabled storage
  }
}

// First-touch capture — runs once when this module is first imported (app startup).
(function captureFirstTouch(): void {
  const ss = safeSession();
  if (!ss) {
    return;
  }

  // Session key in its own try so a crypto/storage failure can't gate referral/campaign capture.
  try {
    if (!ss.getItem(SESSION_KEY)) {
      ss.setItem(SESSION_KEY, crypto.randomUUID());
    }
  } catch {
    // ignore — session key is best-effort
  }

  try {
    const q = new URLSearchParams(window.location.search);

    if (!ss.getItem(LANDING_KEY)) {
      ss.setItem(LANDING_KEY, (window.location.pathname || '/').slice(0, 400)); // first-touch landing page
    }

    const ref = q.get('ub_ref');
    if (ref && !ss.getItem(REF_KEY)) {
      ss.setItem(REF_KEY, ref); // first-touch — do not overwrite
    }

    const utmSource = q.get('utm_source');
    if (utmSource && !ss.getItem(CAMPAIGN_KEY)) {
      // Packed "source|medium|campaign", matching the backend's PackUtm shape. Strip the '|' packing
      // delimiter from each part (as the server's Clean() does) so a utm value containing '|' can't
      // split into extra slots — the backend reads a lossy pack and cannot un-merge it.
      const packPart = (v: string | null): string => (v ?? '').replace(/\|/g, ' ');
      ss.setItem(
        CAMPAIGN_KEY,
        [packPart(utmSource), packPart(q.get('utm_medium')), packPart(q.get('utm_campaign'))].join('|')
      );
    }
  } catch {
    // ignore storage errors — attribution is best-effort
  }
})();

/** Session attribution headers (referrer / campaign / session key). x-ub-client is added by the caller. */
export function getSessionAttributionHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const ss = safeSession();
  if (!ss) {
    return headers;
  }
  try {
    const ref = ss.getItem(REF_KEY);
    const campaign = ss.getItem(CAMPAIGN_KEY);
    const session = ss.getItem(SESSION_KEY);
    const landing = ss.getItem(LANDING_KEY);
    if (ref) headers['x-ub-referrer'] = ref;
    if (campaign) headers['x-ub-campaign'] = campaign;
    if (session) headers['x-ub-session'] = session;
    if (landing) headers['x-ub-landing'] = landing;
  } catch {
    // ignore
  }
  return headers;
}

// Session beacon — fire once per session so marketing attribution persists even without a
// conversion. Idempotent server-side (upsert by session key); rate-limited at Cloudflare (CORE-505).
export function ensureSession(baseUrl: string, clientId: string): void {
  if (!baseUrl) {
    return;
  }
  const ss = safeSession();
  try {
    if (ss && ss.getItem(SENT_KEY)) {
      return; // already sent this session
    }
    if (ss) {
      ss.setItem(SENT_KEY, '1'); // optimistic — endpoint is idempotent
    }
  } catch {
    // ignore
  }
  const headers: Record<string, string> = { 'x-ub-client': clientId, ...getSessionAttributionHeaders() };
  try {
    void fetch(baseUrl.replace(/\/+$/, '') + '/attribution/sessions', {
      method: 'POST',
      headers,
      keepalive: true,
    }).catch(() => {}); // best-effort; never block the app
  } catch {
    // ignore
  }
}
